import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { brl } from "@/lib/format";
import { PageHeader, EmptyState } from "@/components/Common";
import { TableShell } from "@/components/FormKit";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, CheckCircle2, AlertTriangle, XCircle, Minus, Filter, RotateCcw } from "lucide-react";

const MONTHS = ["Acumulado do ano", "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];

const SIT = {
  dentro: { label: "Dentro do orçamento", icon: CheckCircle2, cls: "text-emerald-600" },
  atencao: { label: "Atenção", icon: AlertTriangle, cls: "text-amber-600" },
  critico: { label: "Crítico", icon: AlertTriangle, cls: "text-orange-600" },
  excedido: { label: "Orçamento excedido", icon: XCircle, cls: "text-rose-600" },
  sem_orcamento: { label: "Sem orçamento", icon: Minus, cls: "text-slate-400" },
};

function Situacao({ situacao, testId }) {
  const s = SIT[situacao] || SIT.sem_orcamento;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${s.cls}`} data-testid={testId}>
      <s.icon className="h-4 w-4" /> {s.label}
    </span>
  );
}

export default function BudgetVsActualPage() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(0);
  const [groupBy, setGroupBy] = useState("cost_center");
  const [costCenter, setCostCenter] = useState("");
  const [category, setCategory] = useState("");
  const [costCenters, setCostCenters] = useState([]);
  const [categories, setCategories] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/cost-centers").then((r) => setCostCenters(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data.filter((c) => c.type === "despesa"))).catch(() => {});
  }, []);

  const load = () => {
    setLoading(true);
    const p = new URLSearchParams({ year, group_by: groupBy });
    if (month) p.append("month", month);
    if (costCenter) p.append("cost_center_id", costCenter);
    if (category) p.append("category_id", category);
    api.get(`/budgets-vs-actual?${p.toString()}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  };
  // eslint-disable-next-line
  useEffect(() => { load(); }, []);

  const clear = () => { setMonth(0); setGroupBy("cost_center"); setCostCenter(""); setCategory(""); };

  return (
    <div className="space-y-6">
      <PageHeader title="Orçado x Realizado" subtitle="Comparativo gerencial entre o orçamento e o realizado (regime de caixa)" />

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex flex-wrap items-end gap-3" data-testid="bva-filters">
        <div>
          <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Exercício</label>
          <input type="number" value={year} onChange={(e) => setYear(parseInt(e.target.value) || now.getFullYear())} data-testid="bva-year" className="w-24 bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Período</label>
          <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v))}>
            <SelectTrigger className="w-[180px] text-sm" data-testid="bva-month"><SelectValue /></SelectTrigger>
            <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i} value={String(i)}>{m}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Agrupar por</label>
          <Select value={groupBy} onValueChange={setGroupBy}>
            <SelectTrigger className="w-[190px] text-sm" data-testid="bva-groupby"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="cost_center">Centro de Custo</SelectItem>
              <SelectItem value="category">Categoria</SelectItem>
              <SelectItem value="cost_center_category">Centro + Categoria</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Centro de Custo</label>
          <Select value={costCenter || "all"} onValueChange={(v) => setCostCenter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[180px] text-sm" data-testid="bva-costcenter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {costCenters.map((c) => <SelectItem key={c.id} value={c.id}>{c.code} — {c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Categoria</label>
          <Select value={category || "all"} onValueChange={(v) => setCategory(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[180px] text-sm" data-testid="bva-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <button onClick={load} data-testid="bva-apply" className="bg-brand hover:bg-brand-hover text-white font-medium px-4 py-2 rounded-lg text-sm flex items-center gap-2"><Filter className="h-4 w-4" /> Aplicar</button>
        <button onClick={clear} data-testid="bva-clear" className="border border-slate-200 text-slate-600 font-medium px-3 py-2 rounded-lg text-sm flex items-center gap-2 hover:bg-slate-50"><RotateCcw className="h-4 w-4" /> Limpar</button>
      </div>

      {data && (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="bg-slate-100 text-slate-600 px-2.5 py-1 rounded-md">Regime: <strong>Caixa</strong></span>
          {data.has_budget
            ? <span className="bg-brand-light text-brand px-2.5 py-1 rounded-md">Orçamento {data.year} — {data.budget_status} (v{data.budget_version})</span>
            : <span className="bg-amber-50 text-amber-700 px-2.5 py-1 rounded-md">Nenhum orçamento cadastrado para {data.year}</span>}
        </div>
      )}

      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-brand" /></div>
        : !data || data.rows.length === 0 ? <EmptyState text="Sem dados para os filtros selecionados." />
        : (
        <TableShell testId="bva-table" headers={[
          { label: "Centro / Categoria" }, { label: "Orçado", align: "right" }, { label: "Realizado", align: "right" },
          { label: "Diferença", align: "right" }, { label: "% Exec.", align: "right" }, { label: "Situação" },
        ]}>
          {data.rows.map((r) => (
            <tr key={r.key} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`bva-row-${r.key}`}>
              <td className="px-4 py-3 text-slate-800 font-medium">{r.name}</td>
              <td className="px-4 py-3 text-right font-mono text-slate-600">{brl(r.orcado)}</td>
              <td className="px-4 py-3 text-right font-mono font-semibold text-slate-900">{brl(r.realizado)}</td>
              <td className={`px-4 py-3 text-right font-mono ${r.diferenca < 0 ? "text-rose-600" : "text-emerald-600"}`}>{brl(r.diferenca)}</td>
              <td className="px-4 py-3 text-right font-mono text-slate-600">{r.percent == null ? "—" : `${r.percent}%`}</td>
              <td className="px-4 py-3"><Situacao situacao={r.situacao} testId={`bva-sit-${r.key}`} /></td>
            </tr>
          ))}
          <tr className="bg-slate-50 font-semibold border-t-2 border-slate-200" data-testid="bva-totals">
            <td className="px-4 py-3 text-slate-800">TOTAL</td>
            <td className="px-4 py-3 text-right font-mono text-slate-700">{brl(data.totals.orcado)}</td>
            <td className="px-4 py-3 text-right font-mono text-slate-900">{brl(data.totals.realizado)}</td>
            <td className={`px-4 py-3 text-right font-mono ${data.totals.diferenca < 0 ? "text-rose-600" : "text-emerald-600"}`}>{brl(data.totals.diferenca)}</td>
            <td className="px-4 py-3 text-right font-mono text-slate-700">{data.totals.percent == null ? "—" : `${data.totals.percent}%`}</td>
            <td className="px-4 py-3"><Situacao situacao={data.totals.situacao} testId="bva-sit-total" /></td>
          </tr>
        </TableShell>
      )}
    </div>
  );
}
